import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'

type Props = {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

type State = { error: Error | null }

/** Catches render-time crashes so a broken subtree never blanks the whole app. */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Replace with the app's telemetry sink when one is wired up.
    console.error('Unhandled render error', error, info.componentStack)
  }

  private reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">Something broke</h1>
          <p className="text-muted-foreground text-body">{error.message}</p>
        </div>
        <Button onClick={this.reset}>Reload section</Button>
      </div>
    )
  }
}
